/**
 * Other survivors.
 *
 * Their transforms arrive at the server tick rate, so they are rendered from a
 * short history buffer played back on a deliberate delay. Interpolating
 * between two known-good past states looks far better than extrapolating from
 * the newest one, which snaps and overshoots whenever a packet is late.
 */

import * as THREE from 'three';
import { INTERP_DELAY_MS, LifeState, Stance, PLAYER, ATMOSPHERE } from '@static-pines/shared';
import {
  HELD_TORCH,
  TORCH_AIM_DISTANCE,
  makeTorchModel,
  disposeTorchModel,
} from './Torch.js';
import {
  makeSurvivorFigure,
  poseSurvivorFigure,
  disposeSurvivorFigure,
  type SurvivorFigure,
} from './Survivor.js';

interface Sample {
  t: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
  /**
   * Where they are looking vertically. Replicated all along, but dropped on
   * the floor here until it was noticed that a teammate aiming their torch at
   * their own boots lit half the forest on everyone else's screen.
   */
  pitch: number;
}

/** Everything about another survivor that affects how they are drawn. */
export interface RemoteState {
  flashlightOn: boolean;
  life: number;
  stance: number;
  speaking: boolean;
  /** Server-authoritative spot, or -1. Anyone inside one is not drawn. */
  hidingSpotId: number;
  /** Their place in the name stack above a shared hiding spot. */
  nameStackIndex: number;
}

/** Name tag height above a standing player. */
const NAME_HEIGHT = 2.15;
/**
 * Name tag height above a hiding place, clearing the tallest spot geometry —
 * a locker stands two metres.
 */
const HIDDEN_NAME_HEIGHT = 2.3;
/** Vertical pitch between stacked names. The sprite itself is 0.4 tall. */
const NAME_STACK_SPACING = 0.45;
/** How far the mic indicator floats above its own name. */
const MIC_OFFSET = 0.4;

/**
 * How far a collapsed body is lifted off the terrain.
 *
 * Roughly the torso capsule's radius: rotated flat, the figure's midline sits
 * on the group origin, so without this half of it is buried in the ground.
 */
const DOWNED_LIFT = 0.3;



/**
 * Where a dropped torch comes to rest, relative to the body's feet.
 *
 * Off to one side at about hand height along a prone body, aimed at a point
 * far forward and almost on the deck. The shallow angle is what makes it read
 * as a torch lying in the dirt rather than one still being pointed at
 * something — and the long streak of light it throws is what lets a teammate
 * spot where somebody went down.
 */
const DROPPED_TORCH = { x: 0.35, y: 0.18, z: -1.0 };
const DROPPED_TORCH_AIM = { x: 0.35, y: 0.02, z: -6.0 };

/**
 * The dropped aim expressed as a pitch, so the casing and the beam are placed
 * by one code path instead of two that can drift apart.
 */
const DROPPED_TORCH_PITCH = Math.atan2(
  DROPPED_TORCH_AIM.y - DROPPED_TORCH.y,
  -(DROPPED_TORCH_AIM.z - DROPPED_TORCH.z),
);



/**
 * How far down a crouch takes the whole figure, as a fraction of standing.
 *
 * Taken from the eye heights the local player actually uses, so a crouching
 * teammate's head sits where their own camera would be.
 */
const CROUCH_RATIO = PLAYER.CROUCH_EYE_HEIGHT / PLAYER.EYE_HEIGHT;

/**
 * How fast the pose follows the stance.
 *
 * Fast enough that crouch-standing on the spot is legible as deliberate
 * signalling at a distance - the torch swings about 0.6m and settles inside
 * half a second - and slow enough not to snap.
 */
const CROUCH_RATE = 8;

class RemoteAvatar {
  readonly group = new THREE.Group();
  private readonly buffer: Sample[] = [];
  private readonly torch: THREE.SpotLight;
  /** The visible torch: casing, lens and halo. Sits alongside the SpotLight. */
  private readonly torchModel: THREE.Group;
  private nameSprite: THREE.Sprite;
  private readonly micSprite: THREE.Sprite;
  /** Everything physical, so hiding can hide the person but keep the label. */
  private readonly survivor: SurvivorFigure;
  private readonly figure: THREE.Group;
  /** Smoothed 1 (upright) to CROUCH_RATIO (down). Drives the whole pose. */
  private crouch = 1;
  private bobPhase = 0;
  private currentName: string;

  constructor(name: string) {
    // The same body the local player is drawn as once their own view pulls
    // back — see Survivor.ts for why there is only one builder.
    this.survivor = makeSurvivorFigure();
    this.figure = this.survivor.group;
    this.group.add(this.figure);

    // Their flashlight, so you can see teammates sweeping the trees ahead.
    this.torch = new THREE.SpotLight(
      0xffeecc,
      ATMOSPHERE.FLASHLIGHT_INTENSITY * 0.75,
      ATMOSPHERE.FLASHLIGHT_RANGE,
      ATMOSPHERE.FLASHLIGHT_ANGLE,
      ATMOSPHERE.FLASHLIGHT_PENUMBRA,
      ATMOSPHERE.FLASHLIGHT_DECAY,
    );
    this.torch.position.set(HELD_TORCH.x, HELD_TORCH.y, HELD_TORCH.z);
    this.torch.visible = false;
    this.group.add(this.torch);
    this.group.add(this.torch.target);
    // Level, straight ahead. update() re-aims it from their pitch every frame.
    this.torch.target.position.set(
      HELD_TORCH.x,
      HELD_TORCH.y,
      HELD_TORCH.z - TORCH_AIM_DISTANCE,
    );

    /**
     * Added to the group rather than the figure, like the SpotLight it belongs
     * to: a downed player's body folds flat but their dropped torch has its
     * own place on the ground and must not fold with it.
     */
    this.torchModel = makeTorchModel();
    this.torchModel.visible = false;
    this.group.add(this.torchModel);

    this.currentName = name;
    this.nameSprite = makeNameSprite(name);
    this.nameSprite.position.y = NAME_HEIGHT;
    this.group.add(this.nameSprite);

    // Voice indicator, floating above the name. It exists so that a voice
    // heard in the dark can be attributed at a glance — the audio itself is
    // directional, but a lit mic confirms *which* silhouette is talking.
    this.micSprite = makeMicSprite();
    this.micSprite.position.y = NAME_HEIGHT + MIC_OFFSET;
    this.micSprite.visible = false;
    this.group.add(this.micSprite);
  }

  /**
   * Rebuild the name tag if the name changed — players can rename in the
   * lobby, after their avatar (built at Welcome) already exists everywhere.
   */
  setName(name: string): void {
    if (name === this.currentName) return;
    this.currentName = name;

    const y = this.nameSprite.position.y;
    this.group.remove(this.nameSprite);
    this.nameSprite.material.map?.dispose();
    this.nameSprite.material.dispose();

    this.nameSprite = makeNameSprite(name);
    this.nameSprite.position.y = y;
    this.group.add(this.nameSprite);
  }

  push(sample: Sample): void {
    this.buffer.push(sample);
    // Keep a second of history; anything older can never be interpolated to.
    while (this.buffer.length > 2 && sample.t - this.buffer[0]!.t > 1000) {
      this.buffer.shift();
    }
  }

  update(dt: number, renderTime: number, state: RemoteState): void {
    const { flashlightOn, life, stance, speaking } = state;

    /**
     * A downed player is unconscious, not gone.
     *
     * The body stays in the world exactly where it fell, because a teammate
     * has to be able to find it to bring them round — and a revive mechanic
     * whose target cannot be seen is a mechanic nobody will ever use.
     */
    const downed = life === LifeState.Spectating;
    this.group.visible = true;

    /**
     * Concealment is total. Inside a hiding place the body and its torch are
     * simply not drawn — a figure crouched in plain sight next to a bush,
     * or a spotlight with nobody holding it, would both read as a bug rather
     * than as someone hidden.
     *
     * The name stays, floating over the spot, because the whole point of
     * hiding together is knowing who is already in there.
     */
    const hidden = state.hidingSpotId >= 0 && !downed;
    this.figure.visible = !hidden;
    this.torch.visible = flashlightOn && !hidden;

    /**
     * The crouch, applied to the whole figure rather than to the torso alone.
     *
     * It used to squash only the chest capsule, leaving the head floating at
     * full height above it — which read as a bug rather than as ducking, and
     * gave a distant teammate nothing to see. Scaling the figure from the feet
     * drops the head, the shoulders and the torch together, so crouch-standing
     * on the spot swings the light through about 0.6m and is legible across a
     * clearing. It is the one signal in the game that costs nothing and cannot
     * be heard.
     */
    const crouching = stance === Stance.Crouching || stance === Stance.Hiding;
    this.crouch += ((crouching ? CROUCH_RATIO : 1) - this.crouch) * Math.min(1, dt * CROUCH_RATE);
    poseSurvivorFigure(this.survivor, this.crouch);

    // Collapsed forward onto the ground, face down along their last heading.
    this.figure.rotation.x = downed ? -Math.PI / 2 : 0;
    this.figure.position.y = downed ? DOWNED_LIFT : 0;

    // Resolved before the torch is aimed: the beam follows their head, so it
    // needs the same interpolated look direction the body is placed from.
    const sample = this.sampleAt(renderTime);

    /**
     * Where the torch is and which way it points.
     *
     * The group only carries yaw, so a target at a fixed local point sent
     * every remote torch out horizontally no matter how far up or down its
     * owner was aiming: someone lighting their own boots lit a swathe of
     * forest on everyone else's screen. Local forward is -Z and positive pitch
     * is upward, matching the camera's own convention.
     *
     * Held or dropped, the beam and the casing are placed from the same origin
     * and the same angle — two code paths here would eventually disagree, and
     * a torch whose light leaves at a different angle from its lens is worse
     * than one with no model at all.
     */
    const originX = downed ? DROPPED_TORCH.x : HELD_TORCH.x;
    const originZ = downed ? DROPPED_TORCH.z : HELD_TORCH.z;
    // The held torch rides the crouch with the hand holding it. It is not
    // scaled, only lowered — ducking does not shrink your equipment.
    const originY = downed ? DROPPED_TORCH.y : HELD_TORCH.y * this.crouch;
    const beamPitch = downed ? DROPPED_TORCH_PITCH : (sample?.pitch ?? 0);

    this.torch.position.set(originX, originY, originZ);
    this.torch.target.position.set(
      originX,
      originY + Math.sin(beamPitch) * TORCH_AIM_DISTANCE,
      originZ - Math.cos(beamPitch) * TORCH_AIM_DISTANCE,
    );

    // The casing rides the beam, and is only worth drawing when it is lit —
    // an unlit torch is invisible in this forest anyway.
    this.torchModel.position.set(originX, originY, originZ);
    this.torchModel.rotation.x = beamPitch;
    this.torchModel.visible = this.torch.visible;

    // Stacked so several people in one thicket are all readable, rather than
    // printing their names on top of each other.
    // Tracks the head down into a crouch, so the tag stays over the player
    // rather than hanging in the air where they used to be.
    const nameY = hidden
      ? HIDDEN_NAME_HEIGHT + state.nameStackIndex * NAME_STACK_SPACING
      : NAME_HEIGHT * this.crouch;
    this.nameSprite.position.y = nameY;
    this.micSprite.position.y = nameY + MIC_OFFSET;

    this.micSprite.visible = speaking;
    if (speaking) {
      // A gentle breathing pulse, so a lit mic reads as live rather than as a
      // stuck icon.
      const pulse = 1 + Math.sin(this.bobPhase * 7) * 0.12;
      this.micSprite.scale.set(0.4 * pulse, 0.4 * pulse, 1);
    }

    if (sample) {
      this.group.position.set(sample.x, sample.y, sample.z);
      this.group.rotation.y = sample.yaw;
    }

    this.bobPhase += dt;
    this.nameSprite.rotation.z = 0;
  }

  /** Linear interpolation between the two samples bracketing `time`. */
  private sampleAt(time: number): Sample | null {
    if (this.buffer.length === 0) return null;
    if (this.buffer.length === 1) return this.buffer[0]!;

    for (let i = this.buffer.length - 1; i > 0; i--) {
      const b = this.buffer[i]!;
      const a = this.buffer[i - 1]!;
      if (a.t <= time && time <= b.t) {
        const span = b.t - a.t;
        const t = span > 0 ? (time - a.t) / span : 0;

        let dYaw = b.yaw - a.yaw;
        while (dYaw > Math.PI) dYaw -= Math.PI * 2;
        while (dYaw < -Math.PI) dYaw += Math.PI * 2;

        return {
          t: time,
          x: a.x + (b.x - a.x) * t,
          y: a.y + (b.y - a.y) * t,
          z: a.z + (b.z - a.z) * t,
          yaw: a.yaw + dYaw * t,
          // Straight lerp, no wrap handling: pitch is clamped either side of
          // straight ahead and can never take the short way round the back.
          pitch: a.pitch + (b.pitch - a.pitch) * t,
        };
      }
    }

    // Render time is outside the buffer: clamp rather than extrapolate.
    return time < this.buffer[0]!.t ? this.buffer[0]! : this.buffer[this.buffer.length - 1]!;
  }

  dispose(): void {
    this.nameSprite.material.map?.dispose();
    this.nameSprite.material.dispose();
    this.micSprite.material.map?.dispose();
    this.micSprite.material.dispose();

    // The torch and the body own real geometry as well as textures — every
    // player who leaves mid-match would otherwise leak a casing, a lens, a
    // halo and a figure.
    disposeTorchModel(this.torchModel);
    disposeSurvivorFigure(this.survivor);
  }
}

/**
 * A stroke-drawn microphone on a canvas texture — capsule, cradle, stem —
 * in the soft green that conventionally means "live".
 */
function makeMicSprite(): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;

  // A soft halo behind the glyph. The forest is nearly black, and an unlit
  // stroke simply vanishes into it; the glow is what makes the indicator
  // readable at a glance without turning it into a headlamp.
  const halo = ctx.createRadialGradient(32, 30, 4, 32, 30, 30);
  halo.addColorStop(0, 'rgba(150, 214, 132, 0.5)');
  halo.addColorStop(1, 'rgba(150, 214, 132, 0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, 64, 64);

  ctx.strokeStyle = 'rgba(205, 245, 190, 1)';
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';

  // Capsule body.
  ctx.beginPath();
  ctx.roundRect(24, 8, 16, 26, 8);
  ctx.stroke();
  // Cradle.
  ctx.beginPath();
  ctx.arc(32, 30, 15, 0, Math.PI);
  ctx.stroke();
  // Stem.
  ctx.beginPath();
  ctx.moveTo(32, 45);
  ctx.lineTo(32, 54);
  ctx.stroke();

  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    // Additive: the indicator emits rather than reflects, so it stays
    // legible in a forest that is otherwise almost pure black.
    blending: THREE.AdditiveBlending,
    opacity: 0.95,
  });

  const sprite = new THREE.Sprite(material);
  sprite.scale.set(0.4, 0.4, 1);
  return sprite;
}

function makeNameSprite(name: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.font = '600 30px "Courier New", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(190, 200, 210, 0.85)';
  ctx.fillText(name.slice(0, 14), 128, 32);

  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    // Fades out with distance so the forest never becomes a list of labels.
    opacity: 0.8,
  });

  const sprite = new THREE.Sprite(material);
  sprite.scale.set(1.6, 0.4, 1);
  return sprite;
}

export class RemotePlayers {
  readonly group = new THREE.Group();
  private readonly avatars = new Map<string, RemoteAvatar>();

  constructor() {
    this.group.name = 'remote-players';
  }

  ensure(sessionId: string, name: string): RemoteAvatar {
    let avatar = this.avatars.get(sessionId);
    if (!avatar) {
      avatar = new RemoteAvatar(name);
      this.avatars.set(sessionId, avatar);
      this.group.add(avatar.group);
    }
    // Cheap no-op unless they renamed in the lobby.
    avatar.setName(name);
    return avatar;
  }

  remove(sessionId: string): void {
    const avatar = this.avatars.get(sessionId);
    if (!avatar) return;
    this.group.remove(avatar.group);
    avatar.dispose();
    this.avatars.delete(sessionId);
  }

  has(sessionId: string): boolean {
    return this.avatars.has(sessionId);
  }

  push(sessionId: string, sample: Sample): void {
    this.avatars.get(sessionId)?.push(sample);
  }

  update(dt: number, now: number, states: Map<string, RemoteState>): void {
    const renderTime = now - INTERP_DELAY_MS;
    for (const [sessionId, avatar] of this.avatars) {
      const state = states.get(sessionId);
      avatar.update(dt, renderTime, {
        flashlightOn: state?.flashlightOn ?? false,
        life: state?.life ?? LifeState.Alive,
        stance: state?.stance ?? Stance.Standing,
        speaking: state?.speaking ?? false,
        hidingSpotId: state?.hidingSpotId ?? -1,
        nameStackIndex: state?.nameStackIndex ?? 0,
      });
    }
  }

  clear(): void {
    for (const sessionId of [...this.avatars.keys()]) this.remove(sessionId);
  }
}
